/**
 * The FSA 578 code vocabulary shared by the Farms export and import.
 *
 * The template's crop columns hold FSA 578 *codes* — `commodity_code` (CROP/COMM), `type_code`
 * (VAR/TYPE), `intended_use_code` (INT USE), `practice_type_code` (IRR. PR.), `type_fac_code`
 * (NFAC/FAC) — because the sheet exists to transcribe a literal FSA 578 EZ form. On import the
 * three commodity codes are looked up in `FSA.ARCPLC` / `FSACommodities`, and any row that does
 * not match is dropped, so export has to emit codes rather than the names stored on the crop.
 *
 * Names are not round-tripped literally: the importer re-derives `type_name` and `practice_name`
 * from the county's InsuranceOffer rows (`overrideTypeName` / `overridePracticeName` in
 * `components/Import/ImportService`), which is what makes the practice valid for that county.
 */

const FSA_PRACTICE_CODE_IRRIGATED = "I";
const FSA_PRACTICE_CODE_NON_IRRIGATED = "N";
const FSA_INTENDED_USE_GRAIN = "gr";

/** NFAC/FAC column values: whether the crop follows another crop in the same year. */
export const TYPE_FAC_CODE_FOLLOWING = "FAC";
export const TYPE_FAC_CODE_NOT_FOLLOWING = "NFAC";
export const TYPE_FAC_CODES = [TYPE_FAC_CODE_FOLLOWING, TYPE_FAC_CODE_NOT_FOLLOWING];

const normalize = (value) => (value === null || value === undefined ? "" : String(value).trim().toLowerCase());

/**
 * `practice_name` → FSA 578 irrigation code, covering the county FAC/NFAC variants that
 * `overridePracticeName` selects on import ("Nfac (Non-Irrigated)", "Non-Conventional(Irr)", ...).
 *
 * ponytail: practices with no irrigation sense (Summerfallow, Continuous Cropping) return null —
 * the FSA 578 column cannot express them. Carrying those would need a template column of their own.
 */
export function toFarms578PracticeTypeCode(practice_name) {
  const practice = normalize(practice_name);
  if (practice.includes("non-irr") || practice.includes("non irr")) {
    return FSA_PRACTICE_CODE_NON_IRRIGATED;
  }
  if (practice.includes("irr")) {
    return FSA_PRACTICE_CODE_IRRIGATED;
  }
  return null;
}

/**
 * RMA `practice_name` → NFAC/FAC code, or null when the practice says nothing about following
 * another crop (plain "Irrigated", "Non-Conventional(Irr)", ...). RMA spells the distinction as a
 * prefix — "Nfac (Non-Irrigated)", "Fac (Irrigated)", "Nfac (Non-Irrigated Skip Row)" — and `nfac`
 * has to be tested before `fac` because it contains it.
 */
export function toFarms578TypeFacCode(practice_name) {
  const practice = normalize(practice_name);
  if (/^nfac\b/.test(practice)) {
    return TYPE_FAC_CODE_NOT_FOLLOWING;
  }
  if (/^fac\b/.test(practice)) {
    return TYPE_FAC_CODE_FOLLOWING;
  }
  return null;
}

/** Whether a county practice name carries the given NFAC/FAC code. */
export function matchesFarms578TypeFacCode(practice_name, type_fac_code) {
  const code = normalize(type_fac_code);
  return !!code && normalize(toFarms578TypeFacCode(practice_name)) === code;
}

/**
 * `planted_date` → the sheet's PLANTING DATE column as a plain calendar day.
 *
 * The API returns a date-only column as an ISO timestamp, which lands in the cell as
 * "2025-05-15T00:00:00" — readable by the importer but noise on a form someone reads next to a
 * paper 578. Trimming to the day keeps it legible and, unlike writing a real date cell, cannot
 * shift by a day when Excel round-trips it through UTC.
 */
export function toFarms578SheetDate(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${value.getFullYear()}-${month}-${day}`;
  }

  const isoDay = /^(\d{4}-\d{2}-\d{2})/.exec(String(value).trim());
  return isoDay ? isoDay[1] : String(value).trim();
}

/** County practices to fall back on, most specific first, when the sheet's practice is not offered. */
const IRRIGATED_PRACTICE_PREFERENCE = ["Irrigated", "Nfac (Irrigated)", "Non-Conventional(Irr)", "Fac (Irrigated)"];
const NON_IRRIGATED_PRACTICE_PREFERENCE = [
  "Non-Irrigated",
  "Nfac (Non-Irrigated)",
  "Nfac (Non-Irrigated Skip Row)",
  "Non-Conventional(Non-Irr)",
  "Fac (Non-Irrigated)",
  "Fac (Non-Irrigated Skip Row)",
];

function isIrrigatedPractice(practice_name) {
  const practice = normalize(practice_name);
  if (!practice) return null;
  if (practice.includes("non-irrigated")) return false;
  if (practice.includes("irrigated")) return true;
  return null;
}

/** Maps "Irrigated" / "Non-Irrigated" / null onto the closest practice the county actually offers. */
function resolvePracticeName(practice_name, validPracticeNames) {
  if (validPracticeNames.includes(practice_name)) {
    return practice_name;
  }

  const isIrrigated = isIrrigatedPractice(practice_name);
  if (isIrrigated !== null) {
    const preference = isIrrigated ? IRRIGATED_PRACTICE_PREFERENCE : NON_IRRIGATED_PRACTICE_PREFERENCE;
    const preferred = preference.find((name) => validPracticeNames.includes(name));
    if (preferred) return preferred;

    const looseMatch = validPracticeNames.find((name) =>
      isIrrigated ? normalize(name).includes("irr") : normalize(name).includes("non-irr")
    );
    if (looseMatch) return looseMatch;
  }

  return validPracticeNames.length > 0 ? validPracticeNames[0] : practice_name;
}

/**
 * Chooses the county practice for an imported row.
 *
 * `practice_type_code` only carries irrigation, so on its own the preference order above collapses
 * a county's Fac and Nfac variants onto whichever it lists first. The FSA 578 states NFAC/FAC
 * outright, so when the sheet's `type_fac_code` is filled, narrow the county's practices to the
 * ones that agree with it and let the same irrigation logic choose within that set. A code the
 * county does not offer falls back to the full list rather than forcing a wrong practice.
 *
 * @param {string|null} practice_name Practice from the importer ("Irrigated" / "Non-Irrigated")
 * @param {string[]} validPracticeNames Practices the county's InsuranceOffer rows allow
 * @param {string|null} [type_fac_code] Sheet's NFAC/FAC column, one of {@link TYPE_FAC_CODES}
 */
export function overridePracticeName(practice_name, validPracticeNames, type_fac_code = null) {
  const matchingFac = validPracticeNames.filter((name) => matchesFarms578TypeFacCode(name, type_fac_code));
  return resolvePracticeName(practice_name, matchingFac.length ? matchingFac : validPracticeNames);
}

/**
 * FSACommodities row supplying the code triple the importer matches on.
 *
 * Falls back from an exact commodity + type hit to any typed row for the commodity, because the
 * stored `type_name` is an RMA county type ("No Type Specified", or a county-specific substitute
 * chosen by `overrideTypeName`) and frequently does not equal the xref's FSA type. Rows whose xref
 * `type_name` is null are only a last resort: re-importing one yields a null type, which then
 * fails `validateImportFarms`.
 *
 * A commodity + type repeats across intended uses (grain, forage, grazing, silage, seed), so
 * grain wins the tie — Farm Vault crops are grain, and the code lands in the sheet's INT USE
 * column where a human reads it against the paper 578.
 */
export function findFarms578CommodityXref(crop, commodityXrefs) {
  const commodity = normalize(crop?.commodity_name);
  if (!commodity || !commodityXrefs?.length) {
    return null;
  }

  const forCommodity = commodityXrefs.filter((xref) => normalize(xref.commodity_name) === commodity);
  if (!forCommodity.length) {
    return null;
  }

  const type = normalize(crop.type_name);
  const exact = forCommodity.filter((xref) => normalize(xref.type_name) === type);
  const typed = forCommodity.filter((xref) => normalize(xref.type_name));
  const candidates = exact.length ? exact : typed.length ? typed : forCommodity;

  return candidates.find((xref) => normalize(xref.intended_use_code) === FSA_INTENDED_USE_GRAIN) || candidates[0];
}

/**
 * @param {object} crop ProducerFarmFieldCrop-shaped row
 * @param {object[]} commodityXrefs `FSA.ARCPLC` / `FSACommodities`
 * @returns {object} Shallow clone carrying the FSA 578 code columns
 */
export function reverseFarms578ImportTransformsForExport(crop, commodityXrefs) {
  if (!crop || typeof crop !== "object") {
    return crop;
  }

  const xref = findFarms578CommodityXref(crop, commodityXrefs);

  return {
    ...crop,
    commodity_code: crop.commodity_code ?? xref?.fsa_crop_abbr ?? null,
    type_code: crop.type_code ?? xref?.fsa_commodity_type ?? null,
    intended_use_code: crop.intended_use_code ?? xref?.intended_use_code ?? null,
    practice_type_code: crop.practice_type_code ?? toFarms578PracticeTypeCode(crop.practice_name),
    type_fac_code: crop.type_fac_code ?? toFarms578TypeFacCode(crop.practice_name),
    planted_date: toFarms578SheetDate(crop.planted_date),
  };
}
